import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaClock, FaLock, FaBrain, FaMagic, FaExchangeAlt, FaSave, FaStream } from 'react-icons/fa';
import SeoHelmet from './SeoHelmet';
import MonoButton from '../kits/DesignKit/MonoButton';
import RoughBorder from '../kits/LayoutKit/RoughBorder';
import { PaperGradientBG } from '../kits';
import TerminalScreenshot from "./TerminalScreenshot";

import '@fontsource/poppins/400.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';

const ZenPostStudioLandingPageHero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isZenPostRoute = location.pathname === "/zenpost-studio";

  // Refs für RoughBorder
  const problemCard1Ref = useRef();
  const problemCard2Ref = useRef();
  const problemCard3Ref = useRef();
  const featureCard1Ref = useRef();
  const featureCard2Ref = useRef();
  const featureCard3Ref = useRef();
  const featureCard4Ref = useRef();

  useEffect(() => {
    if (!isZenPostRoute) return;
    window.scrollTo(0, 0);
  }, [isZenPostRoute]);

  const trackCTA = (ctaName) => {
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'ZenPostStudio',
        event_label: ctaName,
      });
    }
  };

  const problems = [
    {
      icon: <FaClock className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Wiederholende Arbeit",
      description: "Gleicher Inhalt, unterschiedliche Formate. Copy-Paste für jede Plattform.",
      ref: problemCard1Ref
    },
    {
      icon: <FaExchangeAlt className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Laufende Kosten",
      description: "Cloud-KI-Dienste kosten monatlich. Für jeden im Team.",
      ref: problemCard2Ref
    },
    {
      icon: <FaLock className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Datenschutz",
      description: "Deine Inhalte gehen durch externe Server. Nicht ideal für sensible Texte.",
      ref: problemCard3Ref
    }
  ];

  const features = [
    {
      icon: <FaMagic className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Transformation",
      description: "Wandle jeden Artikel sofort in 9+ Plattform Posts um.",
      ref: featureCard1Ref
    },
    {
      icon: <FaBrain className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Plattform-Optimiert",
      description: "Automatische Anpassung an Ton, Länge & Format.",
      ref: featureCard2Ref
    },
    {
      icon: <FaSave className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Lokale Dokumente",
      description: "Relevante Ablage auf deinem Desktop für maximale Sicherheit.",
      ref: featureCard3Ref
    },
    {
      icon: <FaStream className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Data Room",
      description: "Erstelle echte Dokumente für deine Projekte.",
      ref: featureCard4Ref
    }
  ];

  return (
    <>
      {isZenPostRoute && (
        <SeoHelmet
          title="ZenPost Studio – Schreiben und Publizieren mit lokaler KI"
          description="Schreibe einmal, erstelle Posts für 9 Plattformen: LinkedIn, dev.to, Medium, X, Reddit und mehr. Kostenlose Desktop-App mit lokaler KI."
          keywords="schreiben, publizieren, multi-plattform, lokale ki, ollama, content editor"
          ogImage="/images/zenpoststudio-og.jpg"
        />
      )}
      <PaperGradientBG variant="zenBlack">
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24 font-mono">

          {/* ========== HERO ========== */}
          <section className="text-center mb-16 sm:mb-24">
            {/* Eyebrow Badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-brand bg-transparent px-4 py-1.5 text-xs sm:text-sm  text-[#EFEBDC] mb-6">
              <span className="h-2 w-2 rounded-full bg-brand" />
              Multi-Plattform Publishing
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#EFEBDC] mb-4">
              Schreibe einmal.
              <span className="block text-brand mt-2">
                Publiziere überall.
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-sm  text-[#EFEBDC] max-w-3xl mx-auto leading-relaxed mb-8">
              Verwandle einen Artikel in Posts für 9+ Plattformen – LinkedIn, dev.to, Medium, X, Reddit, GitHub, YouTube, Hashnode & Notion.
            </p>
          </section>


     <section className="py-8 sm:py-12">
            <TerminalScreenshot
              src="https://denisbitter.de/images/zenPost/zenpost_prevgit.webp"
              alt="ZenPost Studio Content AI"
              caption="preview://willkommen"
            />
          </section>
  

          {/* ========== TRENNER ========== */}
     

          {/* ========== FEATURE SECTION ========== */}
    

          {/* ========== CTA BUTTON ========== */}
          <section className="text-center">
            <MonoButton
              onClick={() => {
                trackCTA('Hero_ZenPostStudio');
                navigate('/zenpost-studio');
              }}
              className="text-sm sm:text-sm px-8 py-4 border-brand bg-transparent"
              underlineColors="ZenBrand"
              textColor="text-brand"
            >
              ZenPost Studio
            </MonoButton>
          </section>

        </div>
      </PaperGradientBG>
    </>
  );
};

export default ZenPostStudioLandingPageHero;
